// Ambulation Zone — PinInspector
//
// A SELF-CONTAINED image inspector. Mount a single <PinInspector /> anywhere
// near the App root (it takes NO props) and every PINNED image across the site
// becomes clickable for an enlarged "inspect" view.
//
// HOW IT TARGETS THE RIGHT IMAGES
// Rather than wiring handlers onto each board (which would mean editing them),
// this listens on the DOCUMENT with event delegation. That means images that
// are added, moved, dragged, or re-rendered later are still covered for free —
// we only ever ask "is the thing under the pointer a pinned image?" at the
// moment of interaction. The pinned images are, across the three boards:
//   - MAIN board art ........ img.art-image      (inside .art-card-wrapper, .tack sibling)
//   - Annex Pinboard photos .. img.pin-art        (inside .pin-card)
//   - RedString evidence ..... img.rs-art         (inside .rs-node.rs-art-node)
// We deliberately DO NOT match plain <img> elsewhere (tacks, notepaper, icons,
// decorative textures) — only the tacked/pinned artwork above.
//
// HOVER vs DRAG
// These boards let you DRAG the photos around. So a plain mousedown→mouseup that
// moved more than a few pixels is treated as a drag and ignored — the lightbox
// only opens on a clean click. The hover affordance (a cursor/click glyph plus
// small monospace "inspect" text) is painted by tracking which pinned image the
// pointer is currently over and floating a fixed overlay on top of it.
//
// The lightbox itself renders through a React portal to <body> so its fixed
// backdrop is never trapped inside a transformed/over-flowed board. Its z-index
// stays just below the crash / x-ray layer (100000) so those can still cover it.

import * as React from "react";
import { createPortal } from "react-dom";
import "./pininspector.css";

// The three pinned-image selectors. Matching ANY of these (via .closest on the
// event target) marks an element as inspectable. Kept as one string so a single
// element.closest() call covers all boards.
const PINNED_SELECTOR = "img.art-image, img.pin-art, img.rs-art";

// A click that wanders farther than this (in px, from pointerdown to pointerup)
// is a drag, not a click — leave it to the board's own drag logic.
const DRAG_SLOP = 5;

function reducedMotion() {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
        return false;
    }
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

// Find the pinned <img> for a given event target, or null if it isn't one.
function pinnedImageFrom(target) {
    if (!target || typeof target.closest !== "function") return null;
    const el = target.closest(PINNED_SELECTOR);
    // closest() can match an ancestor; we specifically want the <img>. Since all
    // selectors are img.*, a match IS the image element itself.
    if (el && el.tagName === "IMG") return el;
    return null;
}

// The small "inspect" affordance that floats over a hovered pinned image. A
// cursor/click glyph + monospace label. Purely presentational; pointer events
// pass through so it never interferes with hover/drag on the image beneath.
function InspectBadge(props) {
    const { rect } = props;
    if (!rect) return null;
    const style = {
        // Anchor to the image's on-screen box (fixed coords from getBoundingClientRect).
        top: rect.top + "px",
        left: rect.left + "px",
        width: rect.width + "px",
        height: rect.height + "px",
    };
    return (
        <div className="pin-inspect-affordance" style={style} aria-hidden="true">
            <span className="pin-inspect-chip">
                {/* cursor / click glyph */}
                <svg
                    className="pin-inspect-cursor"
                    viewBox="0 0 24 24"
                    width="13"
                    height="13"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                >
                    {/* arrow pointer */}
                    <path d="M4 3 L13 12 L8.5 12.5 L11 18 L9 19 L6.5 13.3 L4 16 Z" />
                </svg>
                <span className="pin-inspect-text">inspect</span>
            </span>
        </div>
    );
}

class PinInspector extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            // The image currently being inspected in the lightbox (an <img> element)
            // resolved to { src, alt }. null = lightbox closed.
            lightbox: null,
            // The on-screen rect of the hovered pinned image, for the affordance.
            hoverRect: null,
        };

        this.reduced = reducedMotion();

        // The pinned <img> the pointer is presently over (DOM element, not state).
        this.hoveredEl = null;
        // pointerdown bookkeeping to distinguish click from drag.
        this.downX = 0;
        this.downY = 0;
        this.downEl = null;

        // Focus restoration: the element focused before the lightbox opened.
        this.prevFocus = null;
        this.closeBtnRef = React.createRef();
        this.dialogRef = React.createRef();

        this.handleOver = this.handleOver.bind(this);
        this.handleOut = this.handleOut.bind(this);
        this.handlePointerDown = this.handlePointerDown.bind(this);
        this.handlePointerUp = this.handlePointerUp.bind(this);
        this.handleScrollOrResize = this.handleScrollOrResize.bind(this);
        this.handleKeyDown = this.handleKeyDown.bind(this);
        this.openLightbox = this.openLightbox.bind(this);
        this.closeLightbox = this.closeLightbox.bind(this);
        this.refreshHoverRect = this.refreshHoverRect.bind(this);
    }

    componentDidMount() {
        // Delegated listeners on the document cover every board at once and keep
        // working for images added/moved later. pointerover/out bubble, so a
        // single pair tracks hover across all pinned images.
        document.addEventListener("pointerover", this.handleOver, true);
        document.addEventListener("pointerout", this.handleOut, true);
        document.addEventListener("pointerdown", this.handlePointerDown, true);
        document.addEventListener("pointerup", this.handlePointerUp, true);
        // While hovering, the image may scroll or the board may reflow under a
        // drag — keep the floating affordance glued to the image's live box.
        window.addEventListener("scroll", this.handleScrollOrResize, true);
        window.addEventListener("resize", this.handleScrollOrResize);
        document.addEventListener("keydown", this.handleKeyDown);
    }

    componentWillUnmount() {
        document.removeEventListener("pointerover", this.handleOver, true);
        document.removeEventListener("pointerout", this.handleOut, true);
        document.removeEventListener("pointerdown", this.handlePointerDown, true);
        document.removeEventListener("pointerup", this.handlePointerUp, true);
        window.removeEventListener("scroll", this.handleScrollOrResize, true);
        window.removeEventListener("resize", this.handleScrollOrResize);
        document.removeEventListener("keydown", this.handleKeyDown);
        // Never leave the body locked if we unmount mid-lightbox.
        this.unlockScroll();
    }

    // --- Hover affordance -------------------------------------------------

    handleOver(e) {
        const img = pinnedImageFrom(e.target);
        if (!img) return;
        if (this.hoveredEl === img) return;
        this.hoveredEl = img;
        this.setState({ hoverRect: img.getBoundingClientRect() });
    }

    handleOut(e) {
        const img = pinnedImageFrom(e.target);
        if (!img || img !== this.hoveredEl) return;
        // pointerout fires when moving onto a child; only clear when the pointer
        // has actually left the image (relatedTarget no longer within it).
        const to = e.relatedTarget;
        if (to && img.contains(to)) return;
        this.hoveredEl = null;
        this.setState({ hoverRect: null });
    }

    // Keep the floating affordance aligned while the page scrolls / reflows.
    handleScrollOrResize() {
        if (!this.hoveredEl) return;
        this.refreshHoverRect();
    }

    refreshHoverRect() {
        if (!this.hoveredEl) return;
        // If the hovered image was removed from the DOM, drop the affordance.
        if (!this.hoveredEl.isConnected) {
            this.hoveredEl = null;
            this.setState({ hoverRect: null });
            return;
        }
        this.setState({ hoverRect: this.hoveredEl.getBoundingClientRect() });
    }

    // --- Click vs drag ----------------------------------------------------

    handlePointerDown(e) {
        const img = pinnedImageFrom(e.target);
        if (!img) {
            this.downEl = null;
            return;
        }
        // Only a primary (left / touch / pen) press is a candidate click.
        if (e.button != null && e.button !== 0) {
            this.downEl = null;
            return;
        }
        this.downEl = img;
        this.downX = e.clientX;
        this.downY = e.clientY;
        // NOTE: we do NOT preventDefault / stopPropagation here — the board's own
        // drag handlers must still receive this pointerdown so dragging works.
    }

    handlePointerUp(e) {
        const startEl = this.downEl;
        this.downEl = null;
        if (!startEl) return;

        // Must release over the same pinned image we pressed on.
        const upImg = pinnedImageFrom(e.target);
        if (upImg !== startEl) return;

        // If the pointer wandered past the slop threshold, it was a drag — let
        // the board keep its repositioning and do nothing here.
        const dx = Math.abs(e.clientX - this.downX);
        const dy = Math.abs(e.clientY - this.downY);
        if (dx > DRAG_SLOP || dy > DRAG_SLOP) return;

        // Clean click on a pinned image → inspect it.
        this.openLightbox(startEl);
    }

    // --- Lightbox ---------------------------------------------------------

    openLightbox(img) {
        const src = img.currentSrc || img.src;
        if (!src) return;
        const alt = img.getAttribute("alt") || "pinned image";
        this.prevFocus = document.activeElement;
        this.lockScroll();
        // Clearing the hover affordance keeps the chip from lingering over the dim.
        this.hoveredEl = null;
        this.setState({ lightbox: { src, alt }, hoverRect: null }, () => {
            // Move focus into the dialog so Esc/Tab are scoped and screen readers
            // announce it. Prefer the close button.
            const btn = this.closeBtnRef.current;
            if (btn) btn.focus();
        });
    }

    closeLightbox() {
        if (!this.state.lightbox) return;
        this.unlockScroll();
        this.setState({ lightbox: null }, () => {
            // Restore focus to wherever it was before opening.
            const prev = this.prevFocus;
            if (prev && typeof prev.focus === "function" && prev.isConnected) {
                prev.focus();
            }
            this.prevFocus = null;
        });
    }

    handleKeyDown(e) {
        if (!this.state.lightbox) return;
        if (e.key === "Escape") {
            e.preventDefault();
            this.closeLightbox();
            return;
        }
        // Simple focus trap: keep Tab cycling within the dialog (close button is
        // the only focusable, so just hold focus there).
        if (e.key === "Tab") {
            const btn = this.closeBtnRef.current;
            if (btn) {
                e.preventDefault();
                btn.focus();
            }
        }
    }

    lockScroll() {
        try {
            this._prevBodyOverflow = document.body.style.overflow;
            document.body.style.overflow = "hidden";
        } catch (err) {
            /* defensive — never block close on a style write */
        }
    }

    unlockScroll() {
        try {
            if (this._prevBodyOverflow !== undefined) {
                document.body.style.overflow = this._prevBodyOverflow;
                this._prevBodyOverflow = undefined;
            }
        } catch (err) {
            /* ignore */
        }
    }

    // Backdrop click closes — but ONLY when the click is on the dim backdrop
    // itself, not bubbling up from the image or close button.
    handleBackdropClick(e) {
        if (e.target === e.currentTarget) {
            this.closeLightbox();
        }
    }

    renderLightbox() {
        const { lightbox } = this.state;
        if (!lightbox) return null;
        if (typeof document === "undefined" || !document.body) return null;

        const rootClass =
            "pin-inspect-backdrop" + (this.reduced ? " pin-inspect-reduced" : "");

        const node = (
            <div
                className={rootClass}
                onClick={(e) => this.handleBackdropClick(e)}
                role="presentation"
            >
                <div
                    className="pin-inspect-dialog"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Enlarged image for inspection"
                    ref={this.dialogRef}
                >
                    <button
                        type="button"
                        className="pin-inspect-close"
                        aria-label="Close inspector"
                        ref={this.closeBtnRef}
                        onClick={this.closeLightbox}
                    >
                        <svg
                            viewBox="0 0 24 24"
                            width="20"
                            height="20"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.2"
                            strokeLinecap="round"
                            aria-hidden="true"
                        >
                            <path d="M6 6 L18 18 M18 6 L6 18" />
                        </svg>
                    </button>
                    <img
                        className="pin-inspect-image"
                        src={lightbox.src}
                        alt={lightbox.alt}
                        draggable={false}
                    />
                </div>
            </div>
        );

        return createPortal(node, document.body);
    }

    render() {
        return (
            <React.Fragment>
                {/* Hover affordance only shows when NOT already inspecting. */}
                {!this.state.lightbox && (
                    <InspectBadge rect={this.state.hoverRect} />
                )}
                {this.renderLightbox()}
            </React.Fragment>
        );
    }
}

export default PinInspector;
